'use strict';

import { Effect } from './Effect';
import { assignDeep } from '../../../settings';
import {
  AddEquation,
  CustomBlending, DstAlphaFactor,
  DstColorFactor,
  NoBlending,
  ShaderMaterial,
  UniformsUtils, WebGLRenderTarget,
  Color,
  ZeroFactor, Vector3, MathUtils, DataTexture, RedFormat, FloatType, RepeatWrapping,
} from 'three';
import { SSAOBlurShader, SSAOShader } from 'three/examples/jsm/shaders/SSAOShader';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';
import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise';
import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass';

const DEFAULT_PARAMS = {
  kernelRadius: 8,
  minDistance: 0.005,
  maxDistance: 0.1
};

export class ScreenSpaceAmbientOcclusion extends Effect{
  constructor (camera,params = {}) {
    super();
    const settings = assignDeep(DEFAULT_PARAMS,params);
    this.effect = new LiteSAO(camera,window.innerWidth,window.innerHeight);
  }

  set config(conf) {
    this.active = conf.hasOwnProperty('enabled') ? conf.enabled : conf.active;
    this.effect.uniforms.kernelRadius.value = conf.kernelRadius;
    this.effect.uniforms.minDistance.value = conf.minDistance;
    this.effect.uniforms.maxDistance.value = conf.maxDistance;
  }
}

class LiteSAO extends Pass {
  constructor (camera,width,height) {
    super();
    this.camera = camera;
    this.width = width;
    this.height = height;
    this.noiseTexture = null;
    this.kernel = [];
    this.kernelSize = 32;

    this.generateSampleKernel();
    this.generateRandomKernelRotations();

    this.sao = new ShaderMaterial({
      defines: Object.assign( {}, SSAOShader.defines ),
      uniforms: UniformsUtils.clone( SSAOShader.uniforms ),
      vertexShader: SSAOShader.vertexShader,
      fragmentShader: SSAOShader.fragmentShader,
      blending: NoBlending,
      depthTest: false,
      depthWrite: false
    });

    this.uniforms = this.sao.uniforms;

    this.sao.uniforms.kernel.value = this.kernel;
    this.sao.uniforms.tNoise.value = this.noiseTexture;
    this.sao.uniforms.cameraProjectionMatrix.value.copy(this.camera.projectionMatrix);
    this.sao.uniforms.cameraInverseProjectionMatrix.value.copy( this.camera.projectionMatrixInverse );
    this.sao.uniforms.resolution.value.set(this.width,this.height);
    this.sao.uniforms.cameraNear.value = this.camera.near;
    this.sao.uniforms.cameraFar.value = this.camera.far;

    this.saoBlur = new ShaderMaterial({
      defines: Object.assign( {}, SSAOBlurShader.defines ),
      uniforms: UniformsUtils.clone( SSAOBlurShader.uniforms ),
      vertexShader: SSAOBlurShader.vertexShader,
      fragmentShader: SSAOBlurShader.fragmentShader,
      depthTest: false,
      depthWrite: false
    });

    this.copyMaterial = new ShaderMaterial( {
      uniforms: UniformsUtils.clone( CopyShader.uniforms ),
      vertexShader: CopyShader.vertexShader,
      fragmentShader: CopyShader.fragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blendSrc: DstColorFactor,
      blendDst: ZeroFactor,
      blendEquation: AddEquation,
      blendSrcAlpha: DstAlphaFactor,
      blendDstAlpha: ZeroFactor,
      blendEquationAlpha: AddEquation
    } );

    this.defaultMaterial = new ShaderMaterial({
      uniforms: UniformsUtils.clone( CopyShader.uniforms ),
      vertexShader: CopyShader.vertexShader,
      fragmentShader: CopyShader.fragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    this.saoBlur.uniforms.resolution.value.set(this.width,this.height);
    this.defaultRt = new WebGLRenderTarget(this.width,this.height);
    this.saoRt = new WebGLRenderTarget(this.width,this.height);
    this.blurRt = new WebGLRenderTarget(this.width,this.height);

    this.fsQuad = new FullScreenQuad(null);
    this.originalClearColor = new Color();
    this.needsSwap = false;
  }

  generateSampleKernel() {

    const kernelSize = this.kernelSize;
    const kernel = this.kernel;

    for ( let i = 0; i < kernelSize; i ++ ) {

      const sample = new Vector3();
      sample.x = ( Math.random() * 2 ) - 1;
      sample.y = ( Math.random() * 2 ) - 1;
      sample.z = Math.random();

      sample.normalize();

      let scale = i / kernelSize;
      scale = MathUtils.lerp( 0.1, 1, scale * scale );
      sample.multiplyScalar( scale );

      kernel.push( sample );

    }

  }

  generateRandomKernelRotations() {

    const width = 4, height = 4;

    if ( SimplexNoise === undefined ) {

      console.error( 'THREE.SSAOPass: The pass relies on SimplexNoise.' );

    }

    const simplex = new SimplexNoise();

    const size = width * height;
    const data = new Float32Array( size );

    for ( let i = 0; i < size; i ++ ) {

      const x = ( Math.random() * 2 ) - 1;
      const y = ( Math.random() * 2 ) - 1;
      const z = 0;

      data[ i ] = simplex.noise3d( x, y, z );

    }

    this.noiseTexture = new DataTexture( data, width, height, RedFormat, FloatType );
    this.noiseTexture.wrapS = RepeatWrapping;
    this.noiseTexture.wrapT = RepeatWrapping;
    this.noiseTexture.needsUpdate = true;

  }

  setSize( width, height) {
    this.uniforms.resolution.value.set(width,height);
    this.defaultRt.setSize(width,height);
    this.saoRt.setSize(width,height);
    this.blurRt.setSize(width,height);
    this.uniforms.cameraProjectionMatrix.value.copy(this.camera.projectionMatrix);
    this.uniforms.cameraInverseProjectionMatrix.value.copy( this.camera.projectionMatrixInverse );
  }

  render( renderer, writeBuffer, readBuffer) {
    //Render beauty
    this.defaultMaterial.uniforms.tDiffuse.value = readBuffer.texture;
    this.renderPass(renderer,this.defaultMaterial,this.defaultRt);
    //Sao render
    // this.sao.uniforms.tDiffuse.value = readBuffer.texture;
    this.sao.uniforms.tDepth.value = readBuffer.depthTexture;
    this.sao.uniforms.tNormal.value = readBuffer.depthTexture;
    this.renderPass(renderer,this.sao,this.saoRt);
    //Blur render
    this.saoBlur.uniforms[ 'tDiffuse' ].value = this.saoRt.texture;
    this.renderPass(renderer,this.saoBlur,this.blurRt);

    this.copyMaterial.uniforms[ 'tDiffuse' ].value = this.defaultRt.texture;
    this.copyMaterial.blending = NoBlending;
    this.renderPass( renderer, this.copyMaterial, this.renderToScreen ? null : readBuffer );

    this.copyMaterial.uniforms[ 'tDiffuse' ].value = this.blurRt.texture;
    this.copyMaterial.blending = CustomBlending;
    this.renderPass( renderer, this.copyMaterial, this.renderToScreen ? null : readBuffer );
  }

  renderPass( renderer, passMaterial, renderTarget, clearColor, clearAlpha ) {
    // save original state
    renderer.getClearColor( this.originalClearColor );
    const originalClearAlpha = renderer.getClearAlpha();
    const originalAutoClear = renderer.autoClear;

    renderer.setRenderTarget( renderTarget );

    // setup pass state
    renderer.autoClear = false;
    if ( ( clearColor !== undefined ) && ( clearColor !== null ) ) {

      renderer.setClearColor( clearColor );
      renderer.setClearAlpha( clearAlpha || 0.0 );
      renderer.clear();

    }

    this.fsQuad.material = passMaterial;
    this.fsQuad.render( renderer );

    // restore original state
    renderer.autoClear = originalAutoClear;
    renderer.setClearColor( this.originalClearColor );
    renderer.setClearAlpha( originalClearAlpha );

  }

}